/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useTranslation} from 'react-i18next'
import {Offer} from 'types/entities/Offer'
import {Lead} from 'types/entities/Lead'
import {Company} from 'types/entities/Company'
import Dates from 'utils/Dates'
import {DATE_FORMATS} from 'constants/DateFormats'
import {getCompanyDisplayName} from 'utils/companyUtils'
import {currencyNumberFormat} from 'utils/utility'
import {getOfferPrimaryPackagesCart} from 'utils/offers/offersUtils'

export interface EmailPlaceholder {
    key: string
    label: string
    value?: string | number | null
    category: string
}

export const useEmailPlaceholders = (context?: {
    offer?: Offer | null
    lead?: Lead | null
    company?: Company | null
}) => {
    const { t } = useTranslation()
    const { offer, lead, company: contextCompany } = context || {}

    const company = offer?.company || lead?.company || contextCompany
    const offerTotalPrice = getOfferPrimaryPackagesCart(offer)?.price

    // Resolve Contact
    const contact =
        offer?.contact ||
        company?.contacts?.find((c) => c.id === company.default_contact_id) ||
        company?.contacts?.[0] ||
        lead?.contact

    // Resolve Address
    const address =
        offer?.address ||
        company?.addresses?.find((a) => a.id === company.default_address_id) ||
        company?.addresses?.[0]

    // Resolve Seller from lead (offer doesn't have seller object, only seller_id)
    const seller = lead?.seller

    const placeholders: EmailPlaceholder[] = []

    // Offer Placeholders
    if (offer) {
        const cart = offer.packagesCart
        placeholders.push(
            { key: '{{offer_guid}}', label: t('Offer GUID'), value: offer.guid, category: t('Offer') },
            { key: '{{offer_status}}', label: t('Offer Status'), value: offer.status, category: t('Offer') },
            {
                key: '{{offer_valid_until}}',
                label: t('Offer Valid Until'),
                value: offer.valid_until ? Dates.dayjs(offer.valid_until).format(DATE_FORMATS.DATE) : '',
                category: t('Offer')
            },
            {
                key: '{{offer_total_price}}',
                label: t('Offer Total Price'),
                value: offerTotalPrice == null ? '' : currencyNumberFormat(offerTotalPrice),
                category: t('Offer')
            },
            { key: '{{offer_adults}}', label: t('Adults'), value: cart?.drivers_count_adult, category: t('Offer') },
            { key: '{{offer_children}}', label: t('Children'), value: cart?.drivers_count_child, category: t('Offer') },
            {
                key: '{{offer_event_date}}',
                label: t('Event Date'),
                value: cart?.date ? Dates.dayjs(cart.date).format(DATE_FORMATS.DATE) : '',
                category: t('Offer')
            },
            {
                key: '{{offer_event_hour}}',
                label: t('Event Hour'),
                value: cart?.hour?.toString().padStart(2, '0'),
                category: t('Offer')
            },
            {
                key: '{{offer_event_minute}}',
                label: t('Event Minute'),
                value: cart?.minute?.toString().padStart(2, '0'),
                category: t('Offer')
            },
            {
                key: '{{offer_event_time}}',
                label: t('Event Time'),
                value: cart?.hour !== undefined && cart?.minute !== undefined
                    ? `${cart.hour.toString().padStart(2, '0')}:${cart.minute.toString().padStart(2, '0')}`
                    : '',
                category: t('Offer')
            }
        )
    }

    // Seller Placeholders
    if (seller) {
        placeholders.push({
            key: '{{seller_full_name}}',
            label: t('Seller Full Name'),
            value: seller.name && seller.last_name ? `${seller.name} ${seller.last_name}` : seller.name || seller.last_name || '',
            category: t('Seller')
        })
    }

    // Lead Placeholders
    if (lead) {
        placeholders.push(
            { key: '{{lead_name}}', label: t('Lead Name'), value: lead.name, category: t('Lead') },
            { key: '{{lead_status}}', label: t('Lead Status'), value: lead.status, category: t('Lead') },
            { key: '{{lead_email}}', label: t('Lead Email'), value: lead.email, category: t('Lead') },
            { key: '{{lead_phone}}', label: t('Lead Phone'), value: lead.phone, category: t('Lead') },
            { key: '{{lead_potential_value}}', label: t('Potential Value'), value: lead.potential_value, category: t('Lead') }
        )
    }

    // Company Placeholders
    if (company) {
        placeholders.push(
            { key: '{{company_name}}', label: t('Company Name'), value: getCompanyDisplayName(company), category: t('Company') },
            { key: '{{company_cin}}', label: t('CIN'), value: company.cin, category: t('Company') },
            { key: '{{company_email}}', label: t('Company Email'), value: company.email, category: t('Company') },
            { key: '{{company_phone}}', label: t('Company Phone'), value: company.phone, category: t('Company') },
            { key: '{{company_city}}', label: t('City'), value: company.city, category: t('Company') },
            { key: '{{company_iban}}', label: t('IBAN'), value: company.iban, category: t('Company') }
        )
    }

    // Contact Placeholders
    if (contact) {
        placeholders.push(
            { key: '{{contact_title}}', label: t('Title'), value: contact.title || contact.custom_title, category: t('Contact') },
            { key: '{{contact_gender}}', label: t('Gender'), value: contact.gender, category: t('Contact') },
            { key: '{{contact_first_name}}', label: t('First Name'), value: contact.first_name, category: t('Contact') },
            { key: '{{contact_last_name}}', label: t('Last Name'), value: contact.last_name, category: t('Contact') },
            {
                key: '{{contact_full_name}}',
                label: t('Full Name'),
                value: contact.first_name && contact.last_name ? `${contact.first_name} ${contact.last_name}` : ('name' in contact && typeof contact.name === 'string' ? contact.name : '') || contact.title || '',
                category: t('Contact')
            },
            { key: '{{contact_email}}', label: t('Contact Email'), value: contact.email, category: t('Contact') },
            { key: '{{contact_phone}}', label: t('Contact Phone'), value: contact.phone, category: t('Contact') }
        )
    }

    // Address Placeholders
    if (address) {
        placeholders.push(
            { key: '{{address_street}}', label: t('Street'), value: address.street_name, category: t('Address') },
            { key: '{{address_city}}', label: t('City'), value: address.town, category: t('Address') },
            { key: '{{address_postal_code}}', label: t('Postal Code'), value: address.postal_code, category: t('Address') },
            { key: '{{address_country}}', label: t('Country'), value: address.country, category: t('Address') }
        )
    }

    // Build a map for quick value lookup
    const placeholderMap = new Map<string, string | number | null | undefined>()
    placeholders.forEach((p) => {
        // Extract field name from key like {{contact_gender}} -> contact_gender
        const fieldName = p.key.replace(/^\{\{|\}\}$/g, '')
        placeholderMap.set(fieldName, p.value)
    })

    // Parse conditional syntax: {{field if:value text="output"}}
    const parseConditional = (text: string, highlightMissing: boolean): string => {
        const conditionalRegex = /\{\{(\w+)\s+if:(\w+)\s+text="([^"]+)"\}\}/g
        return text.replace(conditionalRegex, (_match, field, condition, output) => {
            const fieldValue = placeholderMap.get(field)
            if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
                if (!highlightMissing) return _match
                // Field is missing - show red
                return `<span class="email-placeholder-missing" style="color: red; font-weight: 600;">{{${field} if:${condition} text="${output}"}}</span>`
            }
            if (fieldValue.toString().toLowerCase() === condition.toLowerCase()) {
                return output
            }
            return ''
        })
    }

    return {
        placeholders,
        resolvePlaceholders: (text: string, options: { highlightMissing?: boolean; escapeValues?: boolean } = {}) => {
            const { highlightMissing = true, escapeValues = false } = options
            let resolved = text

            // First handle conditional syntax
            resolved = parseConditional(resolved, highlightMissing)

            // Then replace standard placeholders
            placeholders.forEach((p) => {
                const hasValue = p.value !== null && p.value !== undefined && p.value !== ''
                if (hasValue) {
                    const value = p.value?.toString() || ''
                    const replacement = escapeValues
                        ? value.replace(
                              /[&<>"']/g,
                              (character) =>
                                  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]!
                          )
                        : value
                    resolved = resolved.replaceAll(p.key, () => replacement)
                } else if (highlightMissing) {
                    // Show unresolved placeholder in red
                    const redPlaceholder = `<span class="email-placeholder-missing" style="color: red; font-weight: 600;">${p.key}</span>`
                    resolved = resolved.replaceAll(p.key, redPlaceholder)
                }
            })
            return resolved
        },
    }
}
